/**
 * Optimizes image URLs by appending resize parameters.
 * Supports Supabase Storage transforms and Unsplash URL params.
 *
 * Standard sizes (width × height):
 *   card:  400 × 300   – carousel / grid cards (aspect 4:3)
 *   hero:  1200 × 600  – page hero banners
 *   thumb: 200 × 150   – small thumbnails
 */

export type ImagePreset = 'card' | 'hero' | 'thumb';

const PRESETS: Record<ImagePreset, { width: number; height: number }> = {
  card: { width: 400, height: 300 },
  hero: { width: 1200, height: 600 },
  thumb: { width: 200, height: 150 },
};

export function optimizeImageUrl(
  url: string | null | undefined,
  preset: ImagePreset = 'card',
): string {
  if (!url) return '';

  const { width, height } = PRESETS[preset];

  // Supabase Storage – use render/image transform endpoint
  if (url.includes('supabase.co/storage/v1/object/public/')) {
    const transformed = url.replace(
      '/storage/v1/object/public/',
      '/storage/v1/render/image/public/',
    );
    const separator = transformed.includes('?') ? '&' : '?';
    return `${transformed}${separator}width=${width}&height=${height}&resize=cover&quality=75`;
  }

  // Unsplash
  if (url.includes('unsplash.com')) {
    try {
      const u = new URL(url);
      u.searchParams.set('w', String(width));
      u.searchParams.set('h', String(height));
      u.searchParams.set('fit', 'crop');
      u.searchParams.set('q', '75');
      return u.toString();
    } catch {
      return url;
    }
  }

  // Other URLs – return as-is
  return url;
}
